import Combobox from '@/components/common/Combobox';
import {
    Form,
    FormControl,
    FormField,
    FormItem
} from '@/components/common/Form';
import Paginate from '@/components/pagination/Paginate';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { LANGUAGES_SUPPORTED } from '@/types/enums';
import { cn } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LocationDetailPage } from '..';
import LocationItem from './components/LocationItem';
import AreaMapLocation from './components/map/AreaMapLocation';
import TitleList from '@/components/general/title/TitleList';
import { SubTitleWithTwoBox } from '@/components/general/title/sub-title/SubTitle';
import { LocationIcon } from '@/components/icon';
import BannerLayout from '@/components/layout/banner-layout';
import { X } from 'lucide-react';
import ErrorBoundary from '@/components/error-boundary/ErrorBoundary';

export default observer(function AreaLocationPage() {
    // hook
    const {
        t,
        i18n: { language }
    } = useTranslation();

    // store
    const {
        regionProvinceStore: {
            provinces,
            getProvinces,
            regions,
            getRegions,
            getProvincesByRegion
        },
        locationEnduserStore: {
            locationsArea,
            // defaultPagingArea,
            pagingLocationsArea,
            totalRecordLocationsArea,
            totalPageLocationsArea,
            keepSearchParams,
            locationDetail,
            getLocationsArea,
            getLocationsAreaPinMap,
            clean,
            setObservable
        },
        authStore: { verifyToken }
    } = useStore();

    // validate Schema
    const validateSchema = yup.object().shape({
        provinceId: yup.string(),
        regionId: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            provinceId: keepSearchParams?.provinceId ?? '',
            regionId: keepSearchParams?.regionId ?? ''
        }
    });

    // state
    const provinceWatch = form.watch('provinceId');
    const regionWatch = form.watch('regionId');
    // ref
    const locationsRef = useRef<HTMLDivElement>(null);

    // lifecycle
    useEffect(() => {
        // fetch data Regions, Provinces
        getRegions();
        getProvinces();

        return () => {
            clean();
        };
    }, []);

    useEffect(() => {
        // call api check login
        verifyToken();
    }, []);

    useEffect(() => {
        onFetch({ provinceId: provinceWatch, regionId: regionWatch });
    }, [provinceWatch, regionWatch]);

    // function
    const handleFilterProvince = (regionId: number | undefined) => {
        if (regionId) {
            getProvincesByRegion(regionId);
        } else {
            getProvinces();
        }
    };

    const onSearch = async (data: FormData) => {
        onFetch(data);
    };

    const onFetch = async (data: any) => {
        setObservable('keepSearchParams', data);
        if (data?.provinceId || data?.regionId) {
            await flowResult(
                getLocationsArea(
                    {
                        provinceId: data?.provinceId,
                        regionId: data?.regionId
                    },
                    {
                        ...pagingLocationsArea,
                        pagination: {
                            ...pagingLocationsArea.pagination,
                            pageIndex: 0,
                            pageSize: 6
                        }
                    }
                )
            );
            data?.provinceId && scrollToTopLocations();
        }
        await flowResult(
            getLocationsAreaPinMap({
                page: -1,
                provinceId: data?.provinceId,
                regionId: data?.regionId
            })
        );
    };

    const scrollToTopLocations = () => {
        if (locationsRef.current) {
            const elementPosition = locationsRef.current.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - 80;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className='overflow-x-hidden under-wrapper'>
            <BannerLayout
                title='地域・都道府県から拠点を探す'
                subTitle='地域・都道府県から拠点を探す'
                url=''
                className='hidden sm:block'
            />

            <div className='wrapper-inner flex flex-col gap-[min(40px,7.7vw)] mt-[min(120px,15vw)]'>
                <SubTitleWithTwoBox
                    title={(
                        <>
                            地域または都道府県から
                            <br className='block md:hidden' />
                            拠点を選択して下さい
                        </>
                    )}
                    sub='SELECT BASE FROM AREA'
                    titleClassName='break-all title font-semibold text-fs-mid24 leading-[calc(30/24)] sm:text-head2 sm:leading-lh-mid34 md:text-[32px] lg:text-page-title lg:leading-lh-mid36'
                />

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSearch)}>
                        <div className='w-full flex flex-col md:flex-row justify-center items-center flex-wrap gap-[min(20px,2.57vw)] px-[min(40px,5.2vw)] py-[min(40px,7.5vw)] bg-[#F8F7F1] rounded-[20px]'>
                            <FormField
                                control={form.control}
                                name='regionId'
                                render={({ field }) => (
                                    <FormItem className='w-full xsm:w-[270px]'>
                                        <FormControl>
                                            <div className='relative'>
                                                <LocationIcon className='absolute left-[20px] top-1/2 -translate-y-1/2 z-10 size-4 text-primaryWeb cursor-default' />
                                                <Combobox
                                                    search={false}
                                                    className='w-full pl-[42px] h-10 border border-border-gray rounded-[5px] py-[10px] leading-lh-nor14'
                                                    commandListClassName='max-h-[min(200px,35vw)]'
                                                    placeholder='地域から探す'
                                                    value={field.value ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value ?? '');
                                                        form.setValue('regionId', item?.code ?? '');
                                                        form.setValue('provinceId', '');
                                                        handleFilterProvince(item?.code);
                                                        setObservable('locationDetail', undefined);
                                                    }}
                                                    items={
                                                        Array.isArray(regions) && regions?.length > 0 ?
                                                            [
                                                                ...regions
                                                                    .filter(item => item.name)
                                                                    .map(item => ({
                                                                        label:
                                                                            language === LANGUAGES_SUPPORTED.Japanese ?
                                                                                item.nameJP :
                                                                                item.name,
                                                                        value: item.id.toString(),
                                                                        code: item.id
                                                                    }))
                                                            ] :
                                                            []
                                                    }
                                                />
                                            </div>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <X className='h-3.5 w-3.5 text-[#000]' />
                            <FormField
                                control={form.control}
                                name='provinceId'
                                render={({ field }) => (
                                    <FormItem className='w-full xsm:w-[270px]'>
                                        <FormControl>
                                            <div className='relative'>
                                                <LocationIcon className='absolute left-[20px] top-1/2 -translate-y-1/2 z-10 size-4 text-primaryWeb cursor-default' />
                                                <Combobox
                                                    search={false}
                                                    className='w-full pl-[42px] h-10 border border-border-gray rounded-[5px] py-[10px] leading-lh-nor14'
                                                    commandListClassName='max-h-[min(200px,35vw)]'
                                                    placeholder='都道府県から探す'
                                                    value={field.value ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value ?? '');
                                                        form.setValue('provinceId', item?.code ?? '');
                                                        setObservable('locationDetail', undefined);
                                                    }}
                                                    items={
                                                        Array.isArray(provinces) && provinces?.length > 0 ?
                                                            [
                                                                ...provinces
                                                                    .filter(item => item.name)
                                                                    .map(item => ({
                                                                        label:
                                                                            language === LANGUAGES_SUPPORTED.Japanese ?
                                                                                item.nameJP :
                                                                                item.name,
                                                                        value: item.id.toString(),
                                                                        code: item.id
                                                                    }))
                                                            ] :
                                                            []
                                                    }
                                                />
                                            </div>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>
                    </form>
                </Form>

                <div className='h-[min(576px,73.5vw)] flex justify-center items-center'>
                    <ErrorBoundary className='w-full h-full'>
                        <AreaMapLocation />
                    </ErrorBoundary>
                </div>
            </div>

            {(locationDetail && Object.keys(locationDetail)?.length > 0) && (
                <div>
                    <LocationDetailPage showBanner={false} />
                </div>
            )}

            {(provinceWatch || regionWatch) && (
                <div
                    ref={locationsRef}
                    className={cn(
                        'wrapper-inner',
                        (locationDetail && Object.keys(locationDetail)?.length > 0) ? 'pt-0' : 'pt-[min(80px,10.25vw)]'
                    )}
                >
                    <div className='flex flex-col gap-[min(40px,7.7vw)]'>
                        {totalRecordLocationsArea > 0 && (
                            <TitleList
                                title={t('sentences.result_for_prefectures', {
                                    count: totalRecordLocationsArea,
                                    prefectures: provinceWatch ?
                                        locationsArea[0]?.province :
                                        (locationsArea[0]?.region ? locationsArea[0]?.region : '')
                                })}
                            />
                        )}
                        <div className='px-[10px] grid gap-x-[40px] gap-y-[20px] grid-cols-1 point-600:grid-cols-2 point-900:grid-cols-3'>
                            {
                                locationsArea?.length > 0 ?
                                    (
                                        locationsArea?.map(item => (
                                            <LocationItem key={item?.id} item={item} />
                                        ))
                                    ) :
                                    (
                                        <div className='text-center w-full col-span-4 h-[10vh] sm:h-[16vh] lg:h-[20vh]'>
                                            拠点が見つかりませんでした
                                        </div>
                                    )
                            }
                        </div>
                        {locationsArea && locationsArea?.length > 0 && (
                            <div className='px-[10px] flex justify-center'>
                                <Paginate
                                    isPagenateEU
                                    forcePage={pagingLocationsArea.pagination.pageIndex}
                                    pageCount={totalPageLocationsArea}
                                    onPageChange={async ({ selected }) => {
                                        pagingLocationsArea.pagination.pageIndex = selected;
                                        await flowResult(getLocationsArea(keepSearchParams, pagingLocationsArea));
                                        scrollToTopLocations();
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});
